import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Fragment, ReactElement, useMemo } from 'react'
import { appointmentFeedDate, monthGroupLabel } from 'app/lib/activityFeedGrouping'
import moment from 'moment'
import { AutomatedRun } from '../commlog/AutomatedRun'
import { CommLogEntry } from '../commlog/CommLogEntry'
import type { CommLogEntryFields } from 'app/types/comm-log'
import { AppointmentCard } from 'app/components/activities/AppointmentCard'
import { TransactionCard } from 'app/components/activities/TransactionCard'
import { TreatmentPlanCard } from 'app/components/activities/TreatmentPlanCard'
import { FeedDivider } from 'app/components/shared/styled/FeedDivider'
import Spinner from 'app/components/Spinner'
import type { AppointmentJson, TransactionJson } from 'app/types/entities'
import type { PlanWithLines } from 'app/types/treatment'
import { AUTOMATED_RUN_MIN_LENGTH, isHumanCommLogEntry } from 'app/lib/commLog'
import {
  collapseRuns,
  foldMatchingRows,
  groupPmsRecords,
  PMS_APPOINTMENTS,
  PMS_COMM_LOG,
  PMS_TRANSACTIONS,
  PMS_TREATMENT_PLANS,
  PmsRecordKind,
  RECORD_ACTIVITIES,
} from 'app/lib/pmsRecords'

/**
 * One record in the merged feed, discriminated on `kind`.
 *
 * TypeScript narrows `item` from `kind`, so the switch below and the callers'
 * callbacks get the right type without asserting it. The casts this replaces
 * were unchecked: nothing stopped a comm log entry being read as a transaction.
 *
 * Activities stay `unknown`. The panel owns their cards and passes a render
 * function; the feed only needs their dates.
 */
export type FeedRecord =
  | { kind: typeof PMS_APPOINTMENTS; date?: string | null; item: AppointmentJson }
  | { kind: typeof PMS_TRANSACTIONS; date?: string | null; item: TransactionJson }
  | { kind: typeof PMS_TREATMENT_PLANS; date?: string | null; item: PlanWithLines }
  | { kind: typeof PMS_COMM_LOG; date?: string | null; item: CommLogEntryFields }
  | { kind: typeof RECORD_ACTIVITIES; date?: string | null; item: unknown }

export type CommLogFeed = {
  entries: CommLogEntryFields[]
  fetching: boolean
  hasError: boolean
  hasMore: boolean
  hasPatientLink: boolean
  loadOlder: () => void
  retry: () => void
}

type Props = {
  kinds: PmsRecordKind[]
  activities?: { id?: string; date?: string | null; item: unknown }[]
  /** Activity cards need the panel's handlers, so the panel renders them. */
  renderActivity?: (item: unknown, isFirst: boolean, isLast: boolean) => ReactElement | null
  appointments?: AppointmentJson[]
  transactions?: TransactionJson[]
  treatmentPlans?: PlanWithLines[]
  commLog?: CommLogFeed
  /**
   * How records are bucketed. The story reads by month; a ledger reads by day,
   * because a day is the unit money actually moves in.
   */
  groupBy?: `month` | `day`
  /**
   * Folds neighbouring records that are the same thing. Returning a shared key
   * groups them; undefined leaves a record standing alone.
   */
  collapseKey?: (record: FeedRecord) => string | undefined
  /** Renders a folded group. Defaults to the comm log's automated run. */
  renderRun?: (records: FeedRecord[]) => ReactElement
  /**
   * Gathers every matching record in a group into one card, wherever the first
   * sits, rather than only folding neighbours. For records whose repetition is
   * scattered through a month instead of clustered.
   */
  foldKey?: (record: FeedRecord) => string | undefined
  /** Renders a folded set from `foldKey`. */
  renderFold?: (records: FeedRecord[]) => ReactElement
  /** Optional summary rendered on the group divider, e.g. a day's net. */
}

/**
 * One chronological stream across the selected record sources.
 *
 * The four types used to be four tabs, which meant a broken appointment and the
 * text where the patient cancelled it could never appear together. They share a
 * timeline now, and the chips above decide which layers are in it.
 */
export function RecordFeed({
  kinds,
  activities,
  renderActivity,
  appointments,
  transactions,
  treatmentPlans,
  commLog,
  groupBy = `month`,
  collapseKey,
  renderRun,
  foldKey,
  renderFold,
}: Props): ReactElement {
  const groups = useMemo(() => {
    const records: FeedRecord[] = [
      /*
       * Sorted on the composed day and time, not on `date` alone. The list
       * arrives ordered by date only, so two appointments on one day came back
       * in arbitrary order (dev #1023). That fix lived in a per-tab branch this
       * feed replaced, so it moves here with the appointments.
       */
      ...(appointments ?? []).map(
        (item): FeedRecord => ({ kind: PMS_APPOINTMENTS, date: appointmentFeedDate(item), item }),
      ),
      ...(transactions ?? []).map((item): FeedRecord => ({ kind: PMS_TRANSACTIONS, date: item.transactionDate, item })),
      ...(treatmentPlans ?? []).map(
        (item): FeedRecord => ({ kind: PMS_TREATMENT_PLANS, date: item.plan.planCreateDate, item }),
      ),
      ...(commLog?.entries ?? []).map((item): FeedRecord => ({ kind: PMS_COMM_LOG, date: item.commDate, item })),
      ...(activities ?? []).map(
        (entry): FeedRecord => ({
          kind: RECORD_ACTIVITIES,
          date: entry.date,
          item: entry.item,
        }),
      ),
    ]

    const groupLabel =
      groupBy === `day`
        ? (date?: string | null) => (date ? moment.utc(date).format(`MMM D, YYYY`) : `Undated`)
        : monthGroupLabel

    return groupPmsRecords({ records, selected: kinds, groupLabel })
  }, [kinds, activities, appointments, transactions, treatmentPlans, commLog?.entries, groupBy])

  const wantsCommLog = kinds.includes(PMS_COMM_LOG)

  if (wantsCommLog && commLog && !commLog.hasPatientLink && groups.length === 0) {
    return <Empty>This contact is not linked to a PMS patient.</Empty>
  }

  if (wantsCommLog && commLog?.hasError && groups.length === 0) {
    return (
      <div className="px-4 py-10 text-center text-body-sm">
        <ExclamationTriangleIcon className="mx-auto mb-2 h-6 w-6 text-warning-default" />
        <p className="text-default">Could not load the communication log.</p>
        <button type="button" className="mt-2 text-body-xs text-info-default hover:underline" onClick={commLog.retry}>
          Try again
        </button>
      </div>
    )
  }

  if (groups.length === 0) {
    if (wantsCommLog && commLog?.fetching) {
      return (
        <div className="flex w-full items-center justify-center py-10">
          <Spinner size={20} />
        </div>
      )
    }
    return <Empty>Nothing here for the selected types.</Empty>
  }

  return (
    <>
      {groups.map((group) => {
        // Collapsing runs per month group keeps a run from spanning a divider,
        // which would otherwise summarise messages the reader cannot see.
        // Within a day the PMS order is arbitrary, so bring like with like
        // before folding. Otherwise a Payment/Adjustment/Payment/Adjustment day
        // collapses into nothing despite being two pairs.
        const orderedRecords = collapseKey
          ? [...group.records].sort((a, b) => (collapseKey(a) ?? ``).localeCompare(collapseKey(b) ?? ``))
          : group.records

        const collapsed = collapseRuns({
          records: orderedRecords,
          keyFor: (record) =>
            record.kind === PMS_COMM_LOG && !isHumanCommLogEntry(record.item) ? `automated` : collapseKey?.(record),
          minRunLength: collapseKey ? 2 : AUTOMATED_RUN_MIN_LENGTH,
        })

        const rows = foldKey ? foldMatchingRows({ rows: collapsed, keyFor: foldKey }) : collapsed

        return (
          <Fragment key={group.label}>
            <FeedDivider text={group.label} className="px-4" />
            <ul className="flex flex-col gap-y-1.5 px-4">
              {rows.map((row, index) =>
                row.kind === `run` ? (
                  <li key={`run-${group.label}-${index}`}>
                    {/*
                      A folded set and a collapsed run are both "run" rows, so
                      the record itself decides which card renders it.
                    */}
                    {foldKey && foldKey(row.records[0]!) !== undefined && renderFold ? (
                      renderFold(row.records)
                    ) : renderRun ? (
                      renderRun(row.records)
                    ) : (
                      <AutomatedRun entries={row.records.flatMap((r) => (r.kind === PMS_COMM_LOG ? [r.item] : []))} />
                    )}
                  </li>
                ) : (
                  <li key={rowKey(row.record, index)}>
                    <PmsRecordRow
                      record={row.record}
                      renderActivity={renderActivity}
                      hideRecordDates={groupBy === `day`}
                    />
                  </li>
                ),
              )}
            </ul>
          </Fragment>
        )
      })}

      {wantsCommLog && commLog?.hasMore && (
        <div className="px-4 pb-2 pt-1 text-center">
          <button
            type="button"
            disabled={commLog.fetching}
            className="rounded-md border border-default px-3 py-1.5 text-body-xs text-default hover:border-strong disabled:opacity-50"
            onClick={commLog.loadOlder}
          >
            {commLog.fetching ? `Loading...` : `Load older comm log entries`}
          </button>
        </div>
      )}
    </>
  )
}

function Empty({ children }: { children: React.ReactNode }): ReactElement {
  return <div className="px-4 py-10 text-center text-body-sm text-subtle">{children}</div>
}

const rowKey = (record: FeedRecord, index: number) =>
  `${record.kind}-${(record.kind === PMS_TREATMENT_PLANS ? record.item.plan.id : (record.item as { id?: string })?.id) ?? index}`

/**
 * Each record keeps the card it already had. Only the ordering is shared, so a
 * transaction still reads like a transaction next to an appointment.
 *
 * The list item belongs to the caller, which owns the list; this returns only
 * the card.
 */
function PmsRecordRow({
  record,
  renderActivity,
  hideRecordDates,
}: {
  record: FeedRecord
  renderActivity?: (item: unknown, isFirst: boolean, isLast: boolean) => ReactElement | null
  hideRecordDates?: boolean
}): ReactElement | null {
  switch (record.kind) {
    case RECORD_ACTIVITIES:
      return renderActivity?.(record.item, true, true) ?? null
    case PMS_APPOINTMENTS:
      return <AppointmentCard appointment={record.item} isFirst isLast />
    case PMS_TRANSACTIONS:
      return <TransactionCard transaction={record.item} isFirst isLast hideDate={hideRecordDates} />
    case PMS_TREATMENT_PLANS:
      return <TreatmentPlanCard presented={record.item} isFirst isLast />
    case PMS_COMM_LOG:
      return <CommLogEntry entry={record.item} isFirst isLast hideDate={hideRecordDates} />
    default:
      return null
  }
}
